import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Modal, Input as AntInput, Radio } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import Input from '../../../../components/elements/input/Input';
import { sendUserNotification } from '../../../users/api/notificationsApi';
import {
  getHighlightedActivity,
  getOccurrenceStatusDisplayLabel,
} from '../../utils/therapyOccurrencePresentation';

const shippingPickupMethods = new Set([
  'PTT',
  'V',
  'APO/AKS/BEX',
  'APO/V',
  'APO/PTT',
]);

const buildAutoMessage = (therapy) => {
  const pickupMethod = therapy?.userPickupMethod || 'APO';

  if (shippingPickupMethods.has(pickupMethod)) {
    return {
      pushTitle: 'Dostupno',
      pushBody: 'Imate notifikaciju u aplikaciji.',
      historyTitle: 'Dostupno',
      historyBody: 'Dostupno je za preuzimanje ili slanje.',
    };
  }

  if (pickupMethod === 'APO') {
    return {
      pushTitle: 'Dostupno',
      pushBody: 'Imate notifikaciju u aplikaciji.',
      historyTitle: 'Dostupno',
      historyBody:
        'Dostupno za preuzimanje, molimo Vas da dođete u naznačenom periodu.',
    };
  }

  return {
    pushTitle: 'Dostupno',
    pushBody: 'Imate notifikaciju u aplikaciji.',
    historyTitle: 'Podsetnik',
    historyBody:
      'Dostupno. Proverite detalje u aplikaciji.',
  };
};

const SendTherapyReminderModal = ({ isOpen, onClose, therapy, onSent }) => {
  const autoMessage = useMemo(() => buildAutoMessage(therapy), [therapy]);
  const recipientLabel =
    therapy?.userName?.trim() ||
    therapy?.userEmail?.trim() ||
    therapy?.userId ||
    '-';

  const [mode, setMode] = useState('auto');
  const [form, setForm] = useState(autoMessage);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // "Ponudi korisniku odgovore": chat poruka postaje kartica za potvrdu
  // dolaska za mesecnu aktivnost koja je na redu.
  const [offerAnswers, setOfferAnswers] = useState(false);
  // Jedan requestId po namernom slanju. Ponovni pokusaj istog slanja (npr.
  // posle greske u mrezi) salje isti id, pa bek ne kreira novu poruku.
  const [requestId, setRequestId] = useState(() => uuidv4());

  // Mesecna aktivnost na koju se poruka odnosi (aktivnost koja je na redu).
  const highlightedOccurrence = useMemo(() => {
    const highlighted = getHighlightedActivity(
      therapy,
      therapy?.occurrences || []
    );
    return highlighted?.type === 'occurrence'
      ? highlighted.occurrence || null
      : null;
  }, [therapy]);

  useEffect(() => {
    if (!isOpen) return;
    setOfferAnswers(false);
    setRequestId(uuidv4());
    setMode('auto');
    setForm(autoMessage);
    setError('');
    setSuccessMessage('');
    setDone(false);
    setLoading(false);
  }, [autoMessage, isOpen, therapy?._id]);

  useEffect(() => {
    if (mode === 'auto') {
      setForm(autoMessage);
    }
  }, [autoMessage, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSend = async () => {
    if (!therapy?.userId) {
      setError('Korisnik za ovu terapiju nije pronadjen.');
      return;
    }

    const payload =
      mode === 'auto'
        ? { ...autoMessage }
        : {
            pushTitle: form.pushTitle?.trim() || '',
            pushBody: form.pushBody?.trim() || '',
            historyTitle: form.historyTitle?.trim() || '',
            historyBody: form.historyBody?.trim() || '',
          };

    if (
      !payload.pushTitle ||
      !payload.pushBody ||
      !payload.historyTitle ||
      !payload.historyBody
    ) {
      setError('Unesite sva polja notifikacije.');
      return;
    }

    if (offerAnswers) {
      if (!highlightedOccurrence?._id) {
        setError('Terapija nema mesecnu aktivnost koja je na redu.');
        return;
      }
      payload.offerAnswers = true;
      payload.occurrenceId = highlightedOccurrence._id;
      payload.requestId = requestId;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const { ok, message, data } = await sendUserNotification(
      therapy.userId,
      payload
    );

    if (!ok) {
      setError(message);
      setLoading(false);
      return;
    }

    // Sledece slanje je novo, namerno slanje i dobija novi requestId.
    setRequestId(uuidv4());
    // Slanje sa odgovorima menja status obavestenja aktivnosti (Poslata
    // notifikacija), pa tabela treba da se osvezi.
    if (payload.offerAnswers && !data?.duplicate) onSent?.();
    setSuccessMessage(
      data?.duplicate
        ? 'Ovaj podsetnik je vec poslat.'
        : message || 'Podsetnik je uspesno poslat.'
    );
    setDone(true);
    setTimeout(() => {
      setDone(false);
      onClose();
    }, 900);
    setLoading(false);
  };

  const handleCancel = () => {
    setError('');
    setSuccessMessage('');
    setDone(false);
    onClose();
  };

  return (
    <Modal
      className="send_notification_modal"
      title="Posalji podsetnik"
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button
          key="send"
          type="primary"
          onClick={handleSend}
          disabled={loading || done}
          style={{ width: '150px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : (
            'Posalji'
          )}
        </Button>,
      ]}
    >
      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ marginBottom: 12 }}
      >
        <Radio value="auto">Automatski tekst</Radio>
        <Radio value="custom">Rucni unos</Radio>
      </Radio.Group>

      <div autoComplete="off">
        <div style={{ marginBottom: 12 }}>
          <strong>Korisnik:</strong> {recipientLabel}
        </div>
        {therapy?.hasDeviceToken === false && (
          <Alert
            title="Korisnik nije odobrio push notifikacije, ali je moguce kreiranje i slanje notifikacije."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Input
          label="Šifrovani naslov"
          id="pushTitle"
          name="pushTitle"
          placeholder="Šifrovani naslov"
          value={form.pushTitle}
          onChange={handleChange}
          disabled={mode === 'auto'}
        />
        <div style={{ marginTop: 12 }}>
          <AntInput.TextArea
            rows={4}
            name="pushBody"
            placeholder="Šifrovani opis"
            value={form.pushBody}
            onChange={handleChange}
            disabled={mode === 'auto'}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <Input
            label="Detaljan naslov"
            id="historyTitle"
            name="historyTitle"
            placeholder="Detaljan naslov"
            value={form.historyTitle}
            onChange={handleChange}
            disabled={mode === 'auto'}
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <AntInput.TextArea
            rows={4}
            name="historyBody"
            placeholder="Detaljan opis"
            value={form.historyBody}
            onChange={handleChange}
            disabled={mode === 'auto'}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Checkbox
            checked={offerAnswers}
            disabled={!highlightedOccurrence}
            onChange={(e) => setOfferAnswers(e.target.checked)}
          >
            Ponudi korisniku odgovore
          </Checkbox>
          {highlightedOccurrence ? (
            offerAnswers && (
              <div style={{ marginTop: 8 }}>
                <strong>Mesecna aktivnost:</strong>{' '}
                {highlightedOccurrence.sequenceNumber
                  ? `${highlightedOccurrence.sequenceNumber}. - `
                  : ''}
                {highlightedOccurrence.scheduledDate
                  ? dayjs(highlightedOccurrence.scheduledDate).format(
                      'DD.MM.YYYY'
                    )
                  : '-'}{' '}
                ({getOccurrenceStatusDisplayLabel(highlightedOccurrence)})
              </div>
            )
          ) : (
            <div style={{ marginTop: 8 }}>
              Terapija nema mesecnu aktivnost koja je na redu.
            </div>
          )}
        </div>
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

SendTherapyReminderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  onSent: PropTypes.func,
};

export default SendTherapyReminderModal;
